import React, { useEffect, useRef } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import MovieIcon from "@mui/icons-material/Movie";
import { useTranslate } from "react-admin";
import type {
  NotificationTestFormValues,
  WhatsappHeaderMediaType,
} from "../../types/notificationTest";
import {
  WHATSAPP_MEDIA_ACCEPT,
  WHATSAPP_MEDIA_MAX_BYTES,
} from "../../constants";
import { fileToBase64 } from "../../transforms/fileToBase64";

interface Props {
  headerType: WhatsappHeaderMediaType;
}

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const HeaderTypeIcon: React.FC<{ type: WhatsappHeaderMediaType }> = ({
  type,
}) => {
  if (type === "IMAGE") return <ImageIcon fontSize="small" />;
  if (type === "VIDEO") return <MovieIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
};

export const TemplateHeaderMediaField: React.FC<Props> = ({ headerType }) => {
  const translate = useTranslate();
  const { control, setValue, getValues } =
    useFormContext<NotificationTestFormValues>();

  const source = useWatch({ control, name: "message.headerMedia.source" });
  const fileName = useWatch({ control, name: "message.headerMedia.fileName" });
  const fileBase64 = useWatch({
    control,
    name: "message.headerMedia.fileBase64",
  });
  const mimeType = useWatch({ control, name: "message.headerMedia.mimeType" });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileSizeRef = useRef<number>(0);
  const errorRef = useRef<string>("");

  useEffect(() => {
    if (getValues("message.headerMedia.type") !== headerType) {
      setValue("message.headerMedia.type", headerType, { shouldDirty: false });
    }
  }, [headerType, getValues, setValue]);

  const clearFileState = (): void => {
    setValue("message.headerMedia.fileName", "", { shouldDirty: true });
    setValue("message.headerMedia.fileBase64", "", { shouldDirty: true });
    setValue("message.headerMedia.mimeType", "", { shouldDirty: true });
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileSizeRef.current = 0;
    errorRef.current = "";
  };

  const handleSourceChange = (next: "URL" | "UPLOAD"): void => {
    setValue("message.headerMedia.source", next, { shouldDirty: true });
    if (next === "URL") {
      clearFileState();
    } else {
      setValue("message.headerMedia.link", "", { shouldDirty: true });
    }
  };

  const handleFilePick = async (file: File | null): Promise<void> => {
    if (!file) return;
    const maxBytes = WHATSAPP_MEDIA_MAX_BYTES[headerType];
    if (file.size > maxBytes) {
      errorRef.current = translate(
        "resources.test.validation.headerMediaTooLarge",
        { max: formatBytes(maxBytes) },
      );
      clearFileState();
      // Force a re-render to show the error.
      setValue("message.headerMedia.fileName", "", { shouldDirty: true });
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      errorRef.current = "";
      fileSizeRef.current = file.size;
      setValue("message.headerMedia.fileName", file.name, {
        shouldDirty: true,
      });
      setValue("message.headerMedia.fileBase64", base64, { shouldDirty: true });
      setValue(
        "message.headerMedia.mimeType",
        file.type || "application/octet-stream",
        { shouldDirty: true },
      );
    } catch {
      errorRef.current = translate(
        "resources.test.validation.headerMediaReadFailed",
      );
      clearFileState();
    }
  };

  const triggerFilePicker = (): void => {
    fileInputRef.current?.click();
  };

  const renderFileSlot = (): React.ReactNode => {
    if (!fileName) {
      return (
        <Box
          onClick={triggerFilePicker}
          sx={{
            border: "1px dashed",
            borderColor: "primary.light",
            borderRadius: 1,
            p: 2.5,
            textAlign: "center",
            cursor: "pointer",
            color: "text.secondary",
            "&:hover": { borderColor: "primary.main", color: "text.primary" },
          }}
        >
          <Typography variant="body2">
            {translate("resources.test.fields.headerMediaDropzone")}
          </Typography>
          <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
            {translate("resources.test.fields.headerMediaDropzoneHint", {
              max: formatBytes(WHATSAPP_MEDIA_MAX_BYTES[headerType]),
            })}
          </Typography>
        </Box>
      );
    }
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.25,
        }}
      >
        <HeaderTypeIcon type={headerType} />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fileSizeRef.current > 0
              ? `${formatBytes(fileSizeRef.current)} · ${mimeType || ""}`
              : mimeType || ""}
            {fileBase64
              ? ` · ${translate("resources.test.fields.headerMediaReady")}`
              : ""}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={clearFileState} aria-label="remove">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "primary.light",
        borderRadius: 1,
        p: 2,
        mb: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2">
          {translate("resources.test.fields.headerMediaTitle")}
        </Typography>
        <Chip
          size="small"
          color="primary"
          variant="outlined"
          icon={<HeaderTypeIcon type={headerType} />}
          label={translate(
            `resources.test.choices.headerMediaType.${headerType}`,
          )}
        />
      </Stack>

      <RadioGroup
        row
        value={source}
        onChange={(e) => handleSourceChange(e.target.value as "URL" | "UPLOAD")}
        sx={{ mb: 1.5 }}
      >
        <FormControlLabel
          value="URL"
          control={<Radio size="small" />}
          label={translate("resources.test.fields.headerMediaSourceUrl")}
        />
        <FormControlLabel
          value="UPLOAD"
          control={<Radio size="small" />}
          label={translate("resources.test.fields.headerMediaSourceUpload")}
        />
      </RadioGroup>

      {source === "URL" ? (
        <Controller
          name="message.headerMedia.link"
          control={control}
          rules={{
            validate: (v) => {
              const url = (v ?? "").trim();
              if (url === "")
                return translate(
                  "resources.test.validation.headerMediaUrlRequired",
                );
              try {
                const parsed = new URL(url);
                if (
                  parsed.protocol !== "http:" &&
                  parsed.protocol !== "https:"
                ) {
                  return translate(
                    "resources.test.validation.headerMediaUrlInvalid",
                  );
                }
                return true;
              } catch {
                return translate(
                  "resources.test.validation.headerMediaUrlInvalid",
                );
              }
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              required
              size="small"
              label={translate("resources.test.fields.headerMediaUrl")}
              placeholder="https://"
              InputLabelProps={{ shrink: true }}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message ?? " "}
            />
          )}
        />
      ) : (
        <Stack spacing={1.5}>
          {renderFileSlot()}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept={WHATSAPP_MEDIA_ACCEPT[headerType]}
            onChange={(e) => {
              const file = e.target.files?.[0] ?? null;
              void handleFilePick(file);
            }}
          />
          {!fileName && (
            <Button
              variant="outlined"
              size="small"
              onClick={triggerFilePicker}
              sx={{ alignSelf: "flex-start" }}
            >
              {translate("resources.test.actions.headerMediaChooseFile")}
            </Button>
          )}
          {errorRef.current && (
            <Typography variant="caption" color="error">
              {errorRef.current}
            </Typography>
          )}
          {headerType === "DOCUMENT" && (
            <Controller
              name="message.headerMedia.fileName"
              control={control}
              rules={{
                validate: (v) =>
                  (v ?? "").trim() !== "" ||
                  translate(
                    "resources.test.validation.headerMediaFilenameRequired",
                  ),
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  size="small"
                  label={translate("resources.test.fields.headerMediaFilename")}
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    translate("resources.test.fields.headerMediaFilenameHint")
                  }
                />
              )}
            />
          )}
        </Stack>
      )}
    </Box>
  );
};

export default TemplateHeaderMediaField;
